import { computed, inject, signal } from "@angular/core";
import { NavItem } from "../models/ui/profile-nav-item.model";
import { AuthorizationService } from "../services/authorization/authorization.service";
import { AuthorizationStateService } from "../services/authorization/authorization-state.service";
import { UserSessionService } from "../services/user-session.service";
import { ProfileOrganizationScopeService } from "../services/authorization/profile-organization-scope.service";
import { PermissionUtilities } from "../utilities/permission.utilities";

export class BaseProfilePageClass {

    //#region Injected Services
    protected readonly authorizationService = inject(AuthorizationService);
    protected readonly authorizationStateService = inject(AuthorizationStateService);
    protected readonly userSessionService = inject(UserSessionService);
    // Provided by the org-scoped profile pages (organization/member/property) so the tab strip is
    // filtered against the organization on screen rather than the active one. The user profile page
    // deliberately does not provide it — a user has no owning organization — and injects null here.
    protected readonly profileOrganizationScope = inject(ProfileOrganizationScopeService, { optional: true });
    //#endregion

    //#region Nav Items
    public navItems = signal<NavItem[]>([]);
    protected isEntityOwner = signal<boolean>(false);

    public filteredNavItems = computed<NavItem[]>(() => {
        const items = this.navItems();
        const authState = this.authorizationStateService.authorizationState();
        const entityOwner = this.isEntityOwner();

        if (!authState) {
            // An ownerOnly tab is still filtered here: ownership is known from the loaded entity rather
            // than from the authorization state, and a tab that is nobody else's business must not be
            // visible for the moment before that state arrives.
            return items.filter(item =>
                (item.ownerOnly ? entityOwner : true)
                && (!item.permission || item.permission.length === 0)
            );
        }

        const scope = PermissionUtilities.resolveOrganizationScope(undefined, this.profileOrganizationScope, this.userSessionService);

        // No tabs at all until the page knows which organization it is rendering. Returning the
        // permission-free subset here instead would let the auto-select below activate a provisional
        // tab that then visibly jumps once the organization resolves — the member profile's first
        // item, Invoicing, is permission-gated. Tab content is guarded on the loaded entity anyway,
        // so nothing is lost by waiting (DEV-2643).
        if (scope.isPending) {
            return [];
        }

        const filtered = items.filter(item => {
            if (item.ownerOnly) return entityOwner;
            if (!item.permission || item.permission.length === 0) return true;
            if (entityOwner && item.ownerBypass) return true;
            return item.permission.some(permission =>
                this.authorizationService.hasPermission(authState, permission, scope.organizationId)
            );
        });

        if (filtered.length > 0 && !filtered.some(item => item.active)) {
            filtered[0] = { ...filtered[0], active: true };
        }

        return filtered;
    });
    //#endregion

    //#region Tab Management
    public onTabChanged(tabId: string): void {
        this.navItems.update(items => items.map(item => ({ ...item, active: item.id === tabId })));
    }

    public setActiveTab(tabIndex: number): void {
        const items = this.navItems();
        const tab = items[tabIndex];
        if (tab) {
            this.onTabChanged(tab.id);
        }
    }

    public setTabFromUrl(tabId: string): void {
        if (!tabId) return;
        
        const items = this.navItems();
        const newActiveTab = items.find(nav => nav.id === tabId);
        
        if (newActiveTab) {
            this.onTabChanged(newActiveTab.id);
        }
    }
    //#endregion
}
